import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';

export type ModalSheetProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
};

const MODAL_THEME = {
  backdrop: 'rgba(0, 0, 0, 0.60)',
  panelBg: 'linear-gradient(180deg, #010802 0%, #020A05 56%, #121F14 100%)',
  border: '#FFCF2F',
  divider: 'rgba(245, 236, 99, 0.20)',
  title: '#E5E7EB',
  closeBg: 'rgba(53, 53, 53, 0.20)',
  closeText: '#E5E7EB',
  shadow: '0 20px 45px rgba(0,0,0,0.45), inset 0 3px 12px rgba(245,236,99,0.12)',
} as const;

export function ModalSheet({ open, onClose, title, children, footer, className }: ModalSheetProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-5 backdrop-blur-sm" style={{ background: MODAL_THEME.backdrop }}>
      <div
        className={cn(
          'max-h-[90vh] w-full max-w-[382px] overflow-y-auto rounded-2xl border backdrop-blur-xl',
          className,
        )}
        style={{ background: MODAL_THEME.panelBg, borderColor: MODAL_THEME.border, boxShadow: MODAL_THEME.shadow }}
      >
        {title ? (
          <div className="flex items-center justify-between gap-3 px-5 py-4">
            <div className="text-base font-semibold" style={{ color: MODAL_THEME.title }}>{title}</div>
            <button
              type="button"
              onClick={onClose}
              className="flex size-8 shrink-0 items-center justify-center rounded-full border"
              style={{ background: MODAL_THEME.closeBg, borderColor: MODAL_THEME.border, color: MODAL_THEME.closeText }}
              aria-label={t('common.close')}
            >
              <CloseIcon />
            </button>
          </div>
        ) : null}
        <div className="px-5 pb-4">{children}</div>
        {footer ? <div className="border-t px-5 py-4" style={{ borderColor: MODAL_THEME.divider }}>{footer}</div> : null}
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.9073 2.84663C15.256 2.58792 15.7513 2.61637 16.0675 2.93256C16.4153 3.28037 16.4153 3.84452 16.0675 4.19233L10.7599 9.49995L16.0675 14.8076C16.4153 15.1554 16.4153 15.7195 16.0675 16.0673C15.7513 16.3835 15.256 16.412 14.9073 16.1533L14.8077 16.0673L9.50012 10.7597L4.1925 16.0673L4.09289 16.1533C3.74423 16.412 3.24892 16.3835 2.93273 16.0673C2.58492 15.7195 2.58492 15.1554 2.93273 14.8076L8.24035 9.49995L2.93273 4.19233C2.58492 3.84452 2.58492 3.28037 2.93273 2.93256C3.24892 2.61637 3.74423 2.58792 4.09289 2.84663L4.1925 2.93256L9.50012 8.24018L14.8077 2.93256L14.9073 2.84663Z"
        fill="currentColor"
      />
    </svg>
  );
}
